import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import Page from '../components/Page';
import Iconify from '../components/Iconify';

const baseURL = process.env.REACT_APP_SERVICE_BASE_URL;
const POLL_CONVERSATIONS_MS = 5000;
const POLL_MESSAGES_MS = 3000;
const MAX_ATTACHMENT_SIZE = 15 * 1024 * 1024;
const ATTACHMENT_ACCEPT = '.png,.jpg,.jpeg,.webp,.gif,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip';

const getHeaders = () => ({
  token: JSON.parse(localStorage.getItem('user'))?.token,
});

const getErrorMessage = error => {
  const message = error?.response?.data?.message;
  if (Array.isArray(message)) return message.join(', ');
  return message || 'Có lỗi xảy ra. Vui lòng thử lại.';
};

const formatTime = value => {
  if (!value) return '';
  return new Date(value).toLocaleString('vi-VN', {
    hour: '2-digit',
    minute: '2-digit',
    day: '2-digit',
    month: '2-digit',
  });
};

const formatConversationTime = value => {
  if (!value) return '';
  const date = new Date(value);
  const today = new Date();
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const daysAgo = Math.round((startOfToday - startOfDate) / 86400000);
  if (daysAgo === 0) {
    return date.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' });
  }
  if (daysAgo === 1) return 'Hôm qua';
  return date.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' });
};

const normalizeUrl = value => {
  if (!value) return '';
  let url = value;
  if (!/^https?:\/\//i.test(url)) {
    url = `${baseURL}${url.startsWith('/') ? '' : '/'}${url}`;
  }
  if (window.location.protocol === 'https:') url = url.replace(/^http:/i, 'https:');
  return url;
};

const getAvatarUrl = avatar => {
  if (!avatar) return '';
  if (/^https?:\/\//i.test(avatar)) return normalizeUrl(avatar);
  if (avatar.startsWith('/')) return normalizeUrl(avatar);
  return `${baseURL}/static/${avatar}/high`;
};

const getConversationPreview = message => {
  if (message?.content) return message.content;
  if (message?.attachment?.type === 'image') return '📷 Hình ảnh';
  if (message?.attachment) return `📎 ${message.attachment.name || 'File đính kèm'}`;
  return '';
};

const formatFileSize = size => {
  if (!size) return '';
  if (size < 1024 * 1024) return `${Math.max(1, Math.round(size / 1024))} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const parseDepositRequest = content => {
  if (!content?.includes('[YÊU CẦU XÁC NHẬN NẠP XU]')) return null;
  const orderId = content.match(/Mã yêu cầu:\s*([a-f\d]{24})/i)?.[1];
  const code = content.match(/Mã chuyển khoản:\s*([^\n]+)/i)?.[1]?.trim();
  const amountText = content.match(/Số tiền:\s*([^\n]+)/i)?.[1] || '';
  const amount = Number(amountText.replace(/[^\d]/g, ''));
  if (!orderId || !code || !Number.isSafeInteger(amount)) return null;
  return { orderId, code, amount };
};

const parseApprovedDepositOrderId = content => {
  if (!content?.includes('[ĐÃ DUYỆT NẠP XU]')) return '';
  return content.match(/Mã yêu cầu:\s*([a-f\d]{24})/i)?.[1] || '';
};

const formatMoney = value => Number(value || 0).toLocaleString('vi-VN');

export default function ChatPage() {
  const [conversations, setConversations] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [messages, setMessages] = useState([]);
  const [content, setContent] = useState('');
  const [search, setSearch] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [approvalDialog, setApprovalDialog] = useState(null);
  const [approvalAmount, setApprovalAmount] = useState('');
  const [approvalNote, setApprovalNote] = useState('');
  const [approving, setApproving] = useState(false);
  const [snack, setSnack] = useState({ open: false, severity: 'error', message: '' });
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const selectedConversation = conversations.find(item => item.user?.id === selectedUserId) || null;
  const approvedOrderIds = new Set(
    messages.map(message => parseApprovedDepositOrderId(message.content)).filter(Boolean),
  );
  const unreadTotal = conversations.reduce((total, item) => total + Number(item.unreadCount || 0), 0);
  const normalizedSearch = search.trim().toLocaleLowerCase('vi-VN');
  const filteredConversations = normalizedSearch
    ? conversations.filter(item => {
        const user = item.user || {};
        return [user.name, user.email, user.phone]
          .filter(Boolean)
          .some(value => value.toLocaleLowerCase('vi-VN').includes(normalizedSearch));
      })
    : conversations;

  const loadConversations = useCallback((showLoading = false) => {
    if (showLoading) setLoadingConversations(true);
    return axios
      .get(`${baseURL}/chat/admin/conversations?page=1&pageSize=100`, { headers: getHeaders() })
      .then(response => {
        const records = response.data.data?.records || [];
        setConversations(records);
        setSelectedUserId(current => {
          if (current && records.some(item => item.user?.id === current)) return current;
          return records[0]?.user?.id || '';
        });
      })
      .catch(error => {
        setSnack({ open: true, severity: 'error', message: getErrorMessage(error) });
      })
      .finally(() => {
        if (showLoading) setLoadingConversations(false);
      });
  }, []);

  const loadMessages = useCallback((userId, showLoading = false) => {
    if (!userId) return Promise.resolve();
    if (showLoading) setLoadingMessages(true);
    return axios
      .get(`${baseURL}/chat/admin/conversations/${userId}/messages?page=1&pageSize=200`, {
        headers: getHeaders(),
      })
      .then(response => {
        const records = response.data.data?.records || [];
        setMessages(records);
        const hasUnreadUserMessage = records.some(message => message.senderType === 'user' && !message.isRead);
        if (!hasUnreadUserMessage) return null;
        return axios.post(`${baseURL}/chat/admin/conversations/${userId}/read`, null, {
          headers: getHeaders(),
        });
      })
      .then(() => {
        setConversations(current =>
          current.map(item => (item.user?.id === userId ? { ...item, unreadCount: 0 } : item)),
        );
      })
      .catch(error => {
        setSnack({ open: true, severity: 'error', message: getErrorMessage(error) });
      })
      .finally(() => {
        if (showLoading) setLoadingMessages(false);
      });
  }, []);

  useEffect(() => {
    loadConversations(true);
    const timer = window.setInterval(() => loadConversations(false), POLL_CONVERSATIONS_MS);
    return () => window.clearInterval(timer);
  }, [loadConversations]);

  useEffect(() => {
    if (!selectedUserId) {
      setMessages([]);
      return undefined;
    }
    loadMessages(selectedUserId, true);
    const timer = window.setInterval(() => loadMessages(selectedUserId, false), POLL_MESSAGES_MS);
    return () => window.clearInterval(timer);
  }, [loadMessages, selectedUserId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = async () => {
    const value = content.trim();
    if (!selectedUserId || (!value && !selectedFile) || sending) return;
    setSending(true);
    try {
      let attachmentId;
      if (selectedFile) {
        const formData = new FormData();
        formData.append('file', selectedFile);
        const uploadResponse = await axios.post(`${baseURL}/chat/attachments`, formData, {
          headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' },
        });
        attachmentId = uploadResponse.data.data?.fileId;
        if (!attachmentId) throw new Error('Không nhận được mã file sau khi upload.');
      }

      await axios.post(
        `${baseURL}/chat/admin/conversations/${selectedUserId}/messages`,
        {
          ...(value ? { content: value } : {}),
          ...(attachmentId ? { attachmentId } : {}),
        },
        { headers: getHeaders() },
      );
      setContent('');
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await Promise.all([loadMessages(selectedUserId, false), loadConversations(false)]);
    } catch (error) {
      setSnack({ open: true, severity: 'error', message: getErrorMessage(error) });
    } finally {
      setSending(false);
    }
  };

  const selectFile = event => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_ATTACHMENT_SIZE) {
      event.target.value = '';
      setSnack({ open: true, severity: 'error', message: 'File tối đa 15 MB.' });
      return;
    }
    setSelectedFile(file);
  };

  const openApproval = request => {
    setApprovalDialog(request);
    setApprovalAmount(String(request.amount));
    setApprovalNote('');
  };

  const approveDeposit = async () => {
    if (!approvalDialog || approving) return;
    const amount = Number(String(approvalAmount).replace(/[^\d]/g, ''));
    if (!Number.isSafeInteger(amount) || amount < 10000 || amount > 100000000) {
      setSnack({
        open: true,
        severity: 'error',
        message: 'Số tiền duyệt phải từ 10.000 đến 100.000.000 VND.',
      });
      return;
    }

    setApproving(true);
    try {
      const response = await axios.post(
        `${baseURL}/banking/deposit-orders/${approvalDialog.orderId}/approve`,
        {
          userId: selectedUserId,
          amount,
          ...(approvalNote.trim() ? { note: approvalNote.trim() } : {}),
        },
        { headers: getHeaders() },
      );
      setApprovalDialog(null);
      setApprovalAmount('');
      setApprovalNote('');
      setSnack({
        open: true,
        severity: 'success',
        message: response.data.data?.message || `Đã cộng ${formatMoney(amount)} xu cho người dùng.`,
      });
      await Promise.all([loadMessages(selectedUserId, false), loadConversations(false)]);
    } catch (error) {
      setSnack({ open: true, severity: 'error', message: getErrorMessage(error) });
    } finally {
      setApproving(false);
    }
  };

  return (
    <Page title="Chat người dùng">
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={4}>
          <Box>
            <Typography variant="h4">Chat người dùng</Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              Tin nhắn tự động cập nhật sau vài giây
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<Iconify icon="eva:refresh-fill" />}
            onClick={() => loadConversations(true)}
          >
            Làm mới
          </Button>
        </Stack>

        <Card sx={{ height: { xs: 'auto', md: 'calc(100vh - 190px)' }, minHeight: 560 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} sx={{ height: '100%' }}>
            <Box
              sx={{
                width: { xs: '100%', md: 360 },
                minWidth: { md: 360 },
                borderRight: { md: 1 },
                borderBottom: { xs: 1, md: 0 },
                borderColor: 'divider',
                overflowY: 'auto',
                maxHeight: { xs: 280, md: 'none' },
              }}
            >
              <Box sx={{ px: 2.5, py: 2 }}>
                <Typography variant="subtitle1">Hội thoại ({conversations.length})</Typography>
              </Box>
              <Divider />
              {loadingConversations ? (
                <Stack alignItems="center" py={5}>
                  <CircularProgress size={28} />
                </Stack>
              ) : (
                <List disablePadding>
                  {conversations.map(conversation => {
                    const user = conversation.user || {};
                    const selected = user.id === selectedUserId;
                    return (
                      <ListItemButton
                        key={user.id}
                        selected={selected}
                        alignItems="flex-start"
                        onClick={() => setSelectedUserId(user.id)}
                        sx={{ py: 1.5 }}
                      >
                        <ListItemAvatar>
                          <Badge
                            color="error"
                            badgeContent={conversation.unreadCount || 0}
                            invisible={!conversation.unreadCount}
                          >
                            <Avatar src={getAvatarUrl(user.avatar)}>{(user.name || user.email || '?')[0]}</Avatar>
                          </Badge>
                        </ListItemAvatar>
                        <ListItemText
                          primary={user.name || 'Người dùng'}
                          secondary={
                            <>
                              <Typography component="span" variant="caption" color="text.secondary" display="block">
                                {user.email || user.phone || user.id}
                              </Typography>
                              <Typography
                                component="span"
                                variant="body2"
                                color={conversation.unreadCount ? 'text.primary' : 'text.secondary'}
                                fontWeight={conversation.unreadCount ? 600 : 400}
                                noWrap
                                display="block"
                              >
                                {conversation.lastMessage?.senderType === 'admin' ? 'Bạn: ' : ''}
                                {getConversationPreview(conversation.lastMessage)}
                              </Typography>
                            </>
                          }
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1, whiteSpace: 'nowrap' }}>
                          {formatTime(conversation.updatedAt)}
                        </Typography>
                      </ListItemButton>
                    );
                  })}
                  {!conversations.length ? (
                    <Box sx={{ px: 3, py: 6, textAlign: 'center' }}>
                      <Iconify icon="eva:message-circle-outline" width={42} color="text.disabled" />
                      <Typography color="text.secondary" mt={1}>
                        Chưa có hội thoại
                      </Typography>
                    </Box>
                  ) : null}
                </List>
              )}
            </Box>

            <Stack sx={{ flex: 1, minWidth: 0, minHeight: { xs: 500, md: 0 } }}>
              {selectedConversation ? (
                <>
                  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 3, py: 1.5 }}>
                    <Avatar src={getAvatarUrl(selectedConversation.user?.avatar)}>
                      {(selectedConversation.user?.name || '?')[0]}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1">{selectedConversation.user?.name || 'Người dùng'}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {selectedConversation.user?.email || selectedConversation.user?.phone || ''}
                      </Typography>
                    </Box>
                  </Stack>
                  <Divider />

                  <Box sx={{ flex: 1, overflowY: 'auto', px: { xs: 2, md: 3 }, py: 2, bgcolor: 'grey.100' }}>
                    {loadingMessages ? (
                      <Stack alignItems="center" py={5}>
                        <CircularProgress size={28} />
                      </Stack>
                    ) : (
                      <Stack spacing={1.5}>
                        {messages.map(message => {
                          const fromAdmin = message.senderType === 'admin';
                          const depositRequest = fromAdmin ? null : parseDepositRequest(message.content);
                          const depositApproved = depositRequest ? approvedOrderIds.has(depositRequest.orderId) : false;
                          return (
                            <Stack
                              key={message.id}
                              direction="row"
                              spacing={1}
                              alignItems="flex-end"
                              justifyContent={fromAdmin ? 'flex-end' : 'flex-start'}
                            >
                              {!fromAdmin && (
                                <Avatar
                                  src={getAvatarUrl(selectedConversation.user?.avatar)}
                                  alt={selectedConversation.user?.name || 'Người dùng'}
                                  sx={{ width: 34, height: 34, fontSize: 14 }}
                                >
                                  {(selectedConversation.user?.name || selectedConversation.user?.email || '?')[0]}
                                </Avatar>
                              )}
                              <Box
                                sx={{
                                  maxWidth: '78%',
                                  px: 2,
                                  py: 1.25,
                                  borderRadius: 2,
                                  bgcolor: fromAdmin ? 'primary.main' : 'background.paper',
                                  color: fromAdmin ? 'primary.contrastText' : 'text.primary',
                                  boxShadow: 1,
                                }}
                              >
                                {message.attachment?.type === 'image' ? (
                                  <Box
                                    component="a"
                                    href={normalizeUrl(message.attachment.url)}
                                    target="_blank"
                                    rel="noreferrer"
                                    sx={{ display: 'block', mb: message.content ? 1 : 0 }}
                                  >
                                    <Box
                                      component="img"
                                      src={normalizeUrl(message.attachment.url)}
                                      alt={message.attachment.name || 'Ảnh chat'}
                                      sx={{
                                        display: 'block',
                                        maxWidth: 320,
                                        width: '100%',
                                        maxHeight: 300,
                                        objectFit: 'cover',
                                        borderRadius: 1.5,
                                      }}
                                    />
                                  </Box>
                                ) : null}
                                {message.attachment && message.attachment.type !== 'image' ? (
                                  <Button
                                    component="a"
                                    href={normalizeUrl(message.attachment.url)}
                                    target="_blank"
                                    rel="noreferrer"
                                    color={fromAdmin ? 'inherit' : 'primary'}
                                    variant={fromAdmin ? 'outlined' : 'text'}
                                    startIcon={<Iconify icon="eva:file-text-fill" />}
                                    sx={{
                                      mb: message.content ? 1 : 0,
                                      maxWidth: 340,
                                      justifyContent: 'flex-start',
                                      textTransform: 'none',
                                    }}
                                  >
                                    <Box sx={{ textAlign: 'left', minWidth: 0 }}>
                                      <Typography variant="body2" noWrap>
                                        {message.attachment.name || 'File đính kèm'}
                                      </Typography>
                                      <Typography variant="caption" sx={{ opacity: 0.72 }}>
                                        {formatFileSize(message.attachment.size)}
                                      </Typography>
                                    </Box>
                                  </Button>
                                ) : null}
                                {message.content ? (
                                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                                    {message.content}
                                  </Typography>
                                ) : null}
                                {depositRequest ? (
                                  <Box
                                    sx={{
                                      mt: 1.25,
                                      p: 1.25,
                                      borderRadius: 1.5,
                                      bgcolor: depositApproved ? 'success.lighter' : 'warning.lighter',
                                      color: 'text.primary',
                                    }}
                                  >
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      Số tiền khách yêu cầu
                                    </Typography>
                                    <Typography variant="subtitle2" mb={1}>
                                      {formatMoney(depositRequest.amount)} VND
                                    </Typography>
                                    <Button
                                      size="small"
                                      fullWidth
                                      variant={depositApproved ? 'outlined' : 'contained'}
                                      color={depositApproved ? 'success' : 'primary'}
                                      disabled={depositApproved}
                                      startIcon={
                                        <Iconify
                                          icon={
                                            depositApproved ? 'eva:checkmark-circle-2-fill' : 'eva:credit-card-fill'
                                          }
                                        />
                                      }
                                      onClick={() => openApproval(depositRequest)}
                                    >
                                      {depositApproved ? 'Đã duyệt nạp xu' : 'Duyệt và cộng xu'}
                                    </Button>
                                  </Box>
                                ) : null}
                                <Typography
                                  variant="caption"
                                  sx={{ display: 'block', mt: 0.5, opacity: 0.72, textAlign: 'right' }}
                                >
                                  {formatTime(message.createdAt)}
                                </Typography>
                              </Box>
                            </Stack>
                          );
                        })}
                        {!messages.length ? (
                          <Typography align="center" color="text.secondary" py={5}>
                            Chưa có tin nhắn
                          </Typography>
                        ) : null}
                        <div ref={messagesEndRef} />
                      </Stack>
                    )}
                  </Box>

                  <Divider />
                  <Box sx={{ p: 2 }}>
                    {selectedFile ? (
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{ mb: 1.5, px: 1.5, py: 1, bgcolor: 'grey.100', borderRadius: 1.5 }}
                      >
                        <Iconify
                          icon={selectedFile.type.startsWith('image/') ? 'eva:image-fill' : 'eva:file-text-fill'}
                          width={22}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" noWrap>
                            {selectedFile.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatFileSize(selectedFile.size)}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedFile(null);
                            if (fileInputRef.current) fileInputRef.current.value = '';
                          }}
                          disabled={sending}
                        >
                          <Iconify icon="eva:close-fill" />
                        </IconButton>
                      </Stack>
                    ) : null}

                    <Stack direction="row" spacing={1} alignItems="flex-end">
                      <input ref={fileInputRef} type="file" accept={ATTACHMENT_ACCEPT} onChange={selectFile} hidden />
                      <IconButton
                        color="primary"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={sending}
                        sx={{ width: 54, height: 54, border: 1, borderColor: 'divider' }}
                      >
                        <Iconify icon="eva:attach-2-fill" width={25} />
                      </IconButton>
                      <TextField
                        fullWidth
                        multiline
                        maxRows={5}
                        placeholder="Nhập phản hồi…"
                        value={content}
                        onChange={event => setContent(event.target.value)}
                        onKeyDown={event => {
                          if (event.key === 'Enter' && !event.shiftKey) {
                            event.preventDefault();
                            send();
                          }
                        }}
                        disabled={sending}
                      />
                      <Button
                        variant="contained"
                        onClick={send}
                        disabled={sending || (!content.trim() && !selectedFile)}
                        sx={{ minWidth: 105, height: 54 }}
                        startIcon={
                          sending ? (
                            <CircularProgress size={18} color="inherit" />
                          ) : (
                            <Iconify icon="eva:paper-plane-fill" />
                          )
                        }
                      >
                        {sending ? 'Đang gửi' : 'Gửi'}
                      </Button>
                    </Stack>
                  </Box>
                </>
              ) : (
                <Stack flex={1} alignItems="center" justifyContent="center" spacing={1.5} p={4}>
                  <Iconify icon="eva:message-circle-outline" width={64} color="text.disabled" />
                  <Typography variant="h6">Chọn một hội thoại</Typography>
                  <Typography color="text.secondary" align="center">
                    Hội thoại sẽ xuất hiện khi người dùng gửi tin nhắn từ ứng dụng.
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Stack>
        </Card>
      </Container>

      <Dialog
        open={Boolean(approvalDialog)}
        onClose={approving ? undefined : () => setApprovalDialog(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Duyệt yêu cầu nạp xu</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Chỉ duyệt sau khi đã kiểm tra ảnh và xác nhận tiền thực sự vào tài khoản ngân hàng.
          </Alert>
          <Stack spacing={2}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Người nhận xu
              </Typography>
              <Typography variant="subtitle1">
                {selectedConversation?.user?.name || 'Người dùng'} —{' '}
                {selectedConversation?.user?.email || selectedConversation?.user?.phone || selectedUserId}
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={0.5}>
                Mã chuyển khoản: {approvalDialog?.code || ''}
              </Typography>
            </Box>
            <TextField
              autoFocus
              fullWidth
              required
              label="Số tiền thực nhận (VND)"
              value={approvalAmount}
              onChange={event => setApprovalAmount(event.target.value.replace(/[^\d]/g, ''))}
              helperText={`Hệ thống sẽ cộng ${formatMoney(approvalAmount)} xu cho người dùng này.`}
              inputProps={{ inputMode: 'numeric' }}
              disabled={approving}
            />
            <TextField
              fullWidth
              multiline
              minRows={2}
              maxRows={4}
              label="Ghi chú của admin (không bắt buộc)"
              value={approvalNote}
              onChange={event => setApprovalNote(event.target.value)}
              inputProps={{ maxLength: 1000 }}
              disabled={approving}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApprovalDialog(null)} disabled={approving}>
            Hủy
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={approveDeposit}
            disabled={approving}
            startIcon={
              approving ? <CircularProgress size={18} color="inherit" /> : <Iconify icon="eva:checkmark-fill" />
            }
          >
            {approving ? 'Đang cộng xu…' : 'Xác nhận duyệt'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack(current => ({ ...current, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack(current => ({ ...current, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Page>
  );
}
